import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faSignOutAlt, faUser } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import './TopBar.css';
import { adminConfig, isAdminUser } from '../../config/adminConfig';
import { useAuthToken } from '../../contexts/AuthTokenContext';

interface Props {
  userId: string;
  onOpenSettings: () => void;
  onLogout: () => void;
}

export const TopBar: React.FC<Props> = ({
  userId,
  onOpenSettings,
  onLogout,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const userInfoRef = useRef<HTMLDivElement>(null);
  const { account } = useAuthToken();
  const isAdmin = isAdminUser(account?.username || '');
  const showSettings = adminConfig.allowSettingsTabForUsers || isAdmin;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userInfoRef.current && !userInfoRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent dropdown from closing
    onOpenSettings();
    setIsOpen(false); // Close dropdown after clicking
  };

  const handleLogoutClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent dropdown from closing
    onLogout();
    setIsOpen(false); // Close dropdown after clicking
  };

  return (
    <div className="topbar">
      <div className="topbar-content">
        <div 
          ref={userInfoRef}
          className={`user-info ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <FontAwesomeIcon icon={faUser} className="user-icon" />
          <span className="username">{t('auth.welcome')} {userId.split('.')[0]}</span>
          <div className="user-dropdown">
            {showSettings && (
              <button className="dropdown-button" onClick={handleSettingsClick}>
                <FontAwesomeIcon icon={faCog} />
                <span>{t('settings.title')}</span>
              </button>
            )}
            <button className="dropdown-button" onClick={handleLogoutClick}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>{t('auth.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 